import {
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    ThemeProvider,
    Typography,
    createTheme
} from "@mui/material";
import React, { useState, useEffect, Fragment } from "react";
import { PageHeader } from "../../components/PageHeader";
import { useStyles } from "../../common/appStyles";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { PageEnum, Predicates } from "../../types/enums";
import EditIcon from '@mui/icons-material/Edit';
import { showSnackbar } from "../../services/snackbar.service";
import { SchoolDetail } from "./SchoolDetail";
import { GetRegionList, CreateSchool, UpdateSchool, GetAllSchoolExpressionBuilder } from "../../services/user-service";
import { IRegionDetail } from "../../types/regions";
import { ICreateSchoolRequest, ISchoolDetail, ISchoolPaginationResponse, IUpdateSchoolRequest } from "../../types/schools";
import { IExpressionRequest } from "../../types/expression";
import { RedirectToDash } from "../RedirectToDash";
import { paginationExpression } from "../../utilities/ProtoExpression";


/**
 * @remark School admin page is for viewing all {@link ISchoolDetail | schools}.
 * Users can sort the schools by which {@link IRegionDetail | region} they belong too.
 * It has buttons to create or edit an existing school which opens the {@link SchoolDetail} page.
 * @returns a react Html component with a table of all school names, and their email domains.
 */
export function SchoolAdmin() {
    const classes = useStyles();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.SchoolAdmin];
    const [showSchoolDialog, setShowSchoolDialog] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState<ISchoolDetail>();
    const [pageTitle, setPageTitle] = useState("School Admin");
    const [titleText, setTitleText] = useState("");
    const [dropDown, setDropDown] = useState(`{"name":"All","id":0}`);
    const user = useSelector((state: RootState) => state.userDetail);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [noResult, setNoResult] = useState(false);
    const [regions, setRegions] = useState<IRegionDetail[]>([]);
    const [count, setCount] = useState(0);
    const [filteredRows, setFilteredRows] = useState<ISchoolDetail[]>([]);
    const [result, setResult] = useState<ISchoolPaginationResponse | undefined>(undefined);
    const predicateId = 2;
    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            h5: {
                color: '#10AD61'
            },
            body1: {
                color: 'black'
            }
        },
    });

    const getAllSchoolsList = (expression: IExpressionRequest) => {
        GetAllSchoolExpressionBuilder(expression)
            .then(response => {
                setFilteredRows(response.schoolInformation);
                setCount(response.count);
                checkNoFilteredResult(response.schoolInformation);
                setResult(response);
            })
            .catch((error) => {
                showSnackbar('Error Retrieving data', 'error', 5000);
            });
    }

    const getAllRegionsList = () => {
        GetRegionList()
            .then(response => {
                setRegions(response);
            })
            .catch((error) => {
                showSnackbar('Error Retrieving Region data', 'error', 5000);
            });
    }

    const onEditSchool = (row: ISchoolDetail) => {
        setTitleText("Edit School");
        row.emailDomainList = row.emailDomainList[0].split(",");
        setSelectedSchool(row);
        setShowSchoolDialog(true);
    }

    const onAddNewSchool = () => {
        setTitleText("Add School");
        setSelectedSchool({
            id: 0,
            name: "",
            emailDomainList: [],
            regionId: 0
        });
        setShowSchoolDialog(true);
    }

    const handleClose = () => {
        setShowSchoolDialog(false);
    }

    const handleSave = async (request: ICreateSchoolRequest | IUpdateSchoolRequest) => {
        try {
            ("id" in request) ? await UpdateSchool(request) : await CreateSchool(request);
            showSnackbar('Saved Successfully', 'success', 5000);
            setShowSchoolDialog(false);
            getAllRegionsList();
            let option: IRegionDetail = JSON.parse(dropDown);
            let expression: IExpressionRequest;
            if ('id' in request) {
                if (option.id === 0) {
                    expression = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, page);
                    getAllSchoolsList(expression);
                }
                else {
                    expression = paginationExpression("RegionId", option.id, Predicates.Equals, rowsPerPage, page);
                    getAllSchoolsList(expression);
                }
            }
            else {
                setPage(0);
                if (option.id === 0) {
                    expression = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, 0);
                    getAllSchoolsList(expression);
                }
                else {
                    expression = paginationExpression("RegionId", option.id, Predicates.Equals, rowsPerPage, 0);
                    getAllSchoolsList(expression);
                }
            }
        } catch (error: any) {
            showSnackbar(error, 'error', 5000);
        }
    }

    const handleSearch = (input: string) => {
        let option: IRegionDetail = JSON.parse(input);
        setPage(0);
        if (option.id === 0) {
            const expression: IExpressionRequest = paginationExpression("Name", "", predicateId, rowsPerPage, 0);
            getAllSchoolsList(expression);
        }
        else {
            const expression: IExpressionRequest = paginationExpression("RegionId", option.id, Predicates.Equals, rowsPerPage, 0);
            getAllSchoolsList(expression);
        }
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        let option: IRegionDetail = JSON.parse(dropDown);
        let expression: IExpressionRequest = paginationExpression("RegionId", option.id, Predicates.Equals, rowsPerPage, newPage);
        if (option.id !== 0) {
            getAllSchoolsList(expression);
        }
        else {
            expression = paginationExpression("Name", "", predicateId, rowsPerPage, newPage);
            getAllSchoolsList(expression);
        }
    }

    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(+value);
        setPage(0);
        let option: IRegionDetail = JSON.parse(dropDown);
        let expression: IExpressionRequest = paginationExpression("RegionId", option.id, Predicates.Equals, value, 0);
        if (option.id !== 0) {
            getAllSchoolsList(expression);
        }
        else {
            expression = paginationExpression("Name", "", predicateId, value, 0);
            getAllSchoolsList(expression);
        }
    }

    const checkNoFilteredResult = (check) => {
        setNoResult(check.length === 0);
    }

    useEffect(() => {
        try {
            const expression: IExpressionRequest = paginationExpression("Name", "", predicateId, rowsPerPage, 0);
            getAllSchoolsList(expression);
            getAllRegionsList();
        } catch (error) {
            console.error(error);
        }
    }, [currentPage, user]);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                {currentPage?.read &&
                    <Grid
                        container
                        className={classes.formContainer}
                        spacing={1}
                        justifyContent="center"
                        alignItems={'center'}
                    >
                        <Grid item sm={12} className='justify-center' padding="5px">
                            <Grid
                                container
                                spacing={1}
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="center"
                                alignItems="center"
                                className={classes.fixGridMargin}
                            >
                                <Grid item xs={4} md={3}><PageHeader title={pageTitle} onBackClick={undefined} /></Grid>
                                <Grid item xs={12}>
                                    <Grid container justifyContent={"center"} alignItems={"center"} columnSpacing={2} rowSpacing={2} direction={{ xs: 'column', md: 'row' }}>
                                        <Grid item xs={1} md={2.8} lg={2.7} xl={2}>
                                            <Button variant="contained" fullWidth size="large" aria-labelledby="add new region" onClick={() => onAddNewSchool()}>
                                                Add New School
                                            </Button>
                                        </Grid>
                                        <Grid item xs={1.5} >
                                            <FormControl fullWidth size="medium">
                                                <InputLabel>Region</InputLabel>
                                                <Select
                                                    label="Region"
                                                    value={dropDown}
                                                    aria-labelledby="region"
                                                    MenuProps={{
                                                        disableScrollLock: true,
                                                        PaperProps: {
                                                            style: {
                                                                maxHeight: "20vh",
                                                            },
                                                        }
                                                    }}
                                                    onChange={(values) => {
                                                        setDropDown(values.target.value);
                                                        handleSearch(values.target.value);
                                                    }
                                                    }
                                                >
                                                    <MenuItem value={`{"name":"All","id":0}`}>All</MenuItem>
                                                    {regions.map((row) => (
                                                        <MenuItem key={row.id} value={JSON.stringify(row)} aria-labelledby={row.name}>{row.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            justifyContent={'center'}
                            alignItems={"center"}
                            spacing={1}
                            direction={"column"}
                            className={classes.gridPaddingTop}
                        >
                            <Grid item sm={11} className={classes.tableGrid}>
                                {(result !== undefined && currentPage) ? (
                                    noResult || <Paper elevation={3}>
                                        <TableContainer className={classes.tableContainer}>
                                            <Table stickyHeader size="small" aria-label="school table">
                                                <TableHead>
                                                    <TableRow className={classes.tableHead}>
                                                        <TableCell key="schoolName" aria-labelledby="School" style={{ width: '10%' }}>School</TableCell>
                                                        <TableCell key="emailDomainList" aria-labelledby="Email Domain List" style={{ width: '15%' }}>Email Domains</TableCell>
                                                        {currentPage?.update && <TableCell key="actions" align="right" aria-labelledby="Actions" style={{ width: '5%' }}>Actions</TableCell>}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredRows.map((row) => (
                                                        <TableRow
                                                            key={row.id}
                                                            className={classes.table}
                                                        >
                                                            <TableCell align="left" aria-labelledby={row.name}>{row.name}</TableCell>
                                                            <TableCell align="left" aria-labelledby={row.emailDomainList.join(", ")}>{row.emailDomainList.join(", ")}</TableCell>
                                                            <TableCell key="actions" align="right">
                                                                {currentPage?.update &&
                                                                    <Button
                                                                        onClick={e => onEditSchool(row)}
                                                                        className="app-button ml10"
                                                                        color='primary'
                                                                        size="small"
                                                                        variant='outlined'
                                                                        aria-labelledby="Edit School">
                                                                        <EditIcon className="fs20 mr6 icon" /> Edit School
                                                                    </Button>
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 50]}
                                            component="div"
                                            count={count}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={(event) => handleChangeRowsPerPage(event.target.value)}
                                        />
                                    </Paper>)
                                    :
                                    <Grid container direction={"column"} rowSpacing={.5}>
                                        <Grid item xs={12}>
                                            <Skeleton variant='rectangular' width={"100%"} height={"3vh"} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Skeleton variant='rounded' width={'100%'} height={'54vh'} />
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                            <Grid alignSelf={"center"} className={classes.tableNoResult}>
                                {noResult && <FormControl fullWidth><Typography variant="h5">No Results Found</Typography></FormControl>}
                            </Grid>
                        </Grid>
                        {currentPage?.read && <Grid item sm={12}>
                            {showSchoolDialog && <SchoolDetail
                                showDialog={showSchoolDialog}
                                selectedSchool={selectedSchool}
                                titleText={titleText}
                                handleClose={handleClose}
                                handleSave={handleSave}
                            />}
                        </Grid>}
                    </Grid>
                }
                {!currentPage?.read && currentPage?.read !== undefined &&
                    <RedirectToDash />
                }
            </ThemeProvider>
        </Fragment>
    );
}
